import {type UniversalHelpClientOptions as ClientOpts} from '@stripe-internal/universal-help-conversations-client';
import {
  type ClientResponse,
  type Conversation,
  EmailIssueClientUrls,
  type Observability,
  RequestACallClientUrls,
  type RequestAction,
} from '@stripe-internal/conversations-client';
import {
  ConversationsApiClient,
  type ConversationsApiClientOptions,
} from '@stripe-internal/conversations-api-client';
import {InlineDataEventBus} from '@stripe-internal/inline-data';
import {TwilioClient} from '@stripe-internal/twilio-conversations-client';

import {Api} from '../../core/lib/api/api';
import {AppConfig, AppProfile} from '../../core/types';
import {
  type AssistantClient,
  SupportConversationsClient,
} from '../../core/lib/SupportConversationsClient';
import {isDotComEntryPoint} from '../../core/lib/util/conversationUtils';
import {syncStorageModeForThreadsChannel} from './storage';

/**
 * v2 counterpart of buildV1Clients: builds the threads client stack on the
 * conversations API v2 REST client (/api/v2/conversations) instead of the
 * legacy UniversalHelpClient. The caller (V1ConversationsController) selects
 * between the two based on the enableConversationsApiV2 feature flag.
 *
 * NOTE: conversationsApiUrls / conversationsApiClientOpts mirror the legacy
 * assistant's createClient.ts. That duplication is intentional — do NOT
 * extract a shared helper: createClient.ts belongs to the legacy
 * StripeAssistant and is expected to be deprecated once threads is fully
 * rolled out; keeping the stacks independent lets the legacy path be deleted
 * wholesale. Until then, if you change opts here, update that file too (and
 * vice versa).
 *
 * enableConversationsThreads gates three Threads-only, v2-only behaviors here:
 * routing LeaveConversation through the all-in-one /end endpoint (instead of
 * plain conversation deletion), additionally ending the chat on the backend
 * when leaving a live chat conversation, and skipping the local leave on the
 * way into chat entirely, since the v2 endpoints already end the assistant
 * conversation server-side when they proactively create the chat channel. It
 * is NOT passed for the purpose the v1 client uses it for (gating
 * includeInactive in an in-progress lookup) — the v2 REST API resumes
 * conversations directly by id and has no equivalent option.
 */
export function buildV2Clients({
  getConfig,
  getProfile,
  getApi,
  getObservability,
  inlineDataEventBus,
  requestAction,
  getCurrentConversation,
  withHistory,
}: {
  getConfig: () => AppConfig;
  getProfile: () => AppProfile;
  getApi: () => Api;
  getObservability: () => Observability;
  inlineDataEventBus: InlineDataEventBus;
  requestAction: (action: RequestAction) => Promise<ClientResponse>;
  getCurrentConversation?: () => Conversation | null | undefined;
  withHistory: boolean;
}) {
  const {
    audience,
    entryPoint,
    urls: {attachmentDownloadPath},
  } = getConfig();
  const {merchant, featureFlags} = getProfile();

  const emailUrls: EmailIssueClientUrls = {
    emailIssueUrl: '/api/contact-form',
  };

  const racUrls: RequestACallClientUrls = {
    requestACallUrl: '/api/request-a-call',
    verificationCodesUrl: `/api/request-a-call/verification-code?redirectUrl=${document.referrer}`,
    channelsUrl: `/api/eligible-channels/${entryPoint}`,
  };

  const conversationsApiUrls = {
    conversationUrl: '/api/v2/conversations',
    attachmentDownloadPath,
    ...emailUrls,
    ...racUrls,
  };

  const humanAgentGroup = isDotComEntryPoint(entryPoint) ? 'sales' : 'support';

  const deviceToken = featureFlags.enableLiveChatSupportInMobile
    ? new URLSearchParams(window.location.search).get('device_token')
    : null;
  const deviceTokenParam = deviceToken
    ? `&device_token=${encodeURIComponent(deviceToken)}`
    : '';

  const chatUrls = {
    tokenUrl: `/api/chat-token?audience=${audience}&entrypoint=${entryPoint}`,
    conversationUrl: `/api/start-chat?audience=${audience}&entrypoint=${entryPoint}&human_agent_group=${humanAgentGroup}${deviceTokenParam}`,
    ...emailUrls,
    ...racUrls,
  };

  const baseClientOpts: ClientOpts = {
    clientFetch: getApi().fetch,
    observability: getObservability(),
  };

  const chatClientOpts: ClientOpts = {
    ...baseClientOpts,
    urls: chatUrls,
  };

  const conversationsApiClientOpts: ConversationsApiClientOptions = {
    ...baseClientOpts,
    urls: conversationsApiUrls,
    userCountry: merchant?.country,
    enableUhPendingInput: featureFlags.enableUhPendingInput,
    enableRegionHeaderRouting: featureFlags.enableRegionHeaderRouting,
    enableConversationsThreads: featureFlags.enableConversationsThreads,
  };

  const uhClient: AssistantClient = new ConversationsApiClient(
    conversationsApiClientOpts,
  );
  const chatClient = new TwilioClient(chatClientOpts);

  const client = new SupportConversationsClient({
    getConfig,
    getProfile,
    getApi,
    getObservability,
    uhClient,
    chatClient,
    inlineDataEventBus,
    requestAction,
    getCurrentConversation,
    enableCrossTabChatTransitionSync: !withHistory,
    getSyncStorageModeForChannel: (channel, syncStorageMode) =>
      syncStorageModeForThreadsChannel(channel, syncStorageMode, withHistory),
    endChatConversationOnLeave: featureFlags.enableConversationsThreads,
    serverEndsUhConversationOnTransition:
      featureFlags.enableConversationsThreads,
  });

  return {
    client,
    chatClient,
    uhClient,
  };
}
